import React, { useState } from 'react';
import useFloorsStore from '../store/useFloorsStore';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { RiEdit2Line, RiFileCopyLine, RiDeleteBinLine, RiAddLine, RiCloseLine } from 'react-icons/ri';
import useDialogStore from '../store/useDialogStore';
import { motion, AnimatePresence } from 'framer-motion';

const { FiCheck, FiX } = FiIcons;

const FloorCard = ({ floor, isActive, onSwitch, onDuplicate, onDelete, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(floor.name);

  const handleRenameSubmit = () => {
    onRename(floor.id, name);
    setIsEditing(false);
  };

  return (
    <div 
      onClick={() => !isEditing && onSwitch(floor.id)}
      className={`relative group flex flex-col bg-white rounded-xl border-2 transition-all cursor-pointer overflow-hidden ${
        isActive ? 'border-[#1E2937] shadow-md' : 'border-gray-100 hover:border-gray-300'
      }`}
    >
      {/* Thumbnail */}
      <div className="h-28 bg-gray-50 flex items-center justify-center overflow-hidden border-b border-gray-100">
        {floor.thumbnail ? (
          <img src={floor.thumbnail} alt={floor.name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-gray-300 flex flex-col items-center">
            <SafeIcon icon={FiIcons.FiMap} className="text-3xl mb-1" />
            <span className="text-[10px] uppercase font-bold tracking-wider">Brak podglądu</span>
          </div>
        )}
      </div>

      {/* Info & Actions */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          {isEditing ? (
            <div className="flex items-center gap-1 w-full" onClick={e => e.stopPropagation()}>
              <input 
                autoFocus
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRenameSubmit()}
                className="w-full text-xs font-bold border-b border-[#1E2937] outline-none py-0.5"
              />
              <button onClick={handleRenameSubmit} className="text-green-600 hover:bg-green-50 p-1 rounded">
                <SafeIcon icon={FiCheck} />
              </button>
            </div>
          ) : (
            <h4 className="text-xs font-bold text-gray-800 truncate flex-1">{floor.name}</h4>
          )}

          {!isEditing && (
            <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
               <button 
                onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <SafeIcon icon={RiEdit2Line} />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-1">
            <button 
              onClick={(e) => { e.stopPropagation(); onDuplicate(floor.id); }}
              className="p-1.5 text-gray-400 hover:text-[#1E2937] hover:bg-gray-100 rounded-lg transition-colors"
              title="Duplikuj piętro"
            >
              <SafeIcon icon={RiFileCopyLine} />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(floor.id); }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Usuń piętro"
            >
              <SafeIcon icon={RiDeleteBinLine} />
            </button>
          </div>
          
          {isActive && (
            <span className="text-[9px] font-bold text-white bg-[#1E2937] px-2 py-0.5 rounded-full uppercase">Aktywne</span>
          )}
        </div>
      </div>
    </div>
  );
};

const FloorsPanel = () => {
  const { floors, activeFloorId, isPanelOpen, togglePanel, switchFloor, addFloor, duplicateFloor, removeFloor, renameFloor } = useFloorsStore();
  const { openDialog } = useDialogStore();

  if (!isPanelOpen) return null;

  const handleDelete = (id) => {
    const floor = floors.find(f => f.id === id);
    openDialog({
      title: 'Usuń Piętro',
      message: `Czy na pewno chcesz usunąć piętro "${floor?.name}"? Tej operacji nie można cofnąć.`,
      confirmText: 'Usuń',
      variant: 'danger',
      onConfirm: () => removeFloor(id)
    });
  };

  return (
    <motion.div 
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      className="absolute right-4 top-16 w-64 bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200 z-50 flex flex-col max-h-[80vh]"
    >
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
          Zarządzanie Piętrami
          <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{floors.length}</span>
        </h3>
        <button onClick={togglePanel} className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
          <SafeIcon icon={RiCloseLine} className="text-xl" />
        </button>
      </div>

      <div className="p-4 overflow-y-auto flex-1 space-y-4 custom-scrollbar">
        {floors.map(floor => (
          <FloorCard 
            key={floor.id}
            floor={floor}
            isActive={floor.id === activeFloorId}
            onSwitch={switchFloor}
            onDuplicate={duplicateFloor}
            onDelete={handleDelete}
            onRename={renameFloor}
          />
        ))}
      </div>

      <div className="p-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
        <button 
          onClick={() => addFloor()}
          className="w-full py-2.5 bg-[#1E2937] text-white rounded-xl flex items-center justify-center gap-2 font-bold text-xs hover:bg-gray-800 transition-all shadow-md active:scale-95"
        >
          <SafeIcon icon={RiAddLine} className="text-lg" />
          DODAJ NOWE PIĘTRO
        </button>
      </div>
    </motion.div>
  );
};

export default FloorsPanel;